import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconSearch from "../../../img/icons/search.svg";
import iconXClose from "../../../img/icons/x-close.svg";

import styles from "./index.module.scss";

// dangerouslySetInnerHTML is omitted because Immutable<> strips the nominal
// brand from TrustedHTML, making the spread below unassignable to <input>
// props (and a search field should never accept injected HTML anyway).
type Props = Immutable<
    Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        "onSearch" | "dangerouslySetInnerHTML"
    >
> & {
    onSearch: (searchTerm: string) => void;
};

const handleFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    fieldRef: React.RefObject<HTMLInputElement | null>,
    setTerms: React.Dispatch<React.SetStateAction<string[]>>,
    onSearch: (searchTerm: string) => void,
): void => {
    e.preventDefault();
    const field = fieldRef.current;
    if (field && field.value.trim()) {
        setTerms((prevTerms) => {
            const newTerms = [...prevTerms, field.value.trim()];
            // Call onSearch with the updated terms
            onSearch(newTerms.join(" "));
            return newTerms;
        });
        field.value = "";
    }
};

const handleRemoveTerm = (
    indexToRemove: number,
    setTerms: React.Dispatch<React.SetStateAction<string[]>>,
    onSearch: (searchTerm: string) => void,
): void => {
    setTerms((prevTerms) => {
        const newTerms = prevTerms.filter(
            (_, index) => index !== indexToRemove,
        );
        // Call onSearch with the updated terms
        onSearch(newTerms.join(" "));
        return newTerms;
    });
};

const handleClearAll = (
    setTerms: React.Dispatch<React.SetStateAction<string[]>>,
    onSearch: (searchTerm: string) => void,
): void => {
    setTerms([]);
    onSearch("");
};

export const SearchField = (props: Props): React.ReactNode => {
    const { onSearch, ...inputProps } = props;
    const [terms, setTerms] = React.useState<string[]>([]);
    const fieldRef = React.useRef<HTMLInputElement>(null);

    return (
        <>
            <form
                className="search-form"
                onSubmit={(e) =>
                    handleFormSubmit(e, fieldRef, setTerms, onSearch)
                }
            >
                <div className="search-form__search">
                    <label
                        className="ada-screenreader-only"
                        htmlFor="search-box"
                    >
                        {t`Search posts`}
                    </label>
                    <input
                        ref={fieldRef}
                        className="search-form__input"
                        {...inputProps}
                        id="search-box"
                        placeholder={t`Search posts`}
                    />

                    <button
                        className="search-form__magnifying-glass"
                        type="submit"
                        aria-label="search TempurLove posts"
                    >
                        <SVG
                            src={iconSearch}
                            title={t`Search`}
                            className={styles.searchIcon}
                            role="presentation"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </form>
            {/* Pills */}
            {terms.length > 0 && (
                <section className="search-tags">
                    {terms.map((term, index) => (
                        <button
                            key={term + index.toString()}
                            onClick={() =>
                                handleRemoveTerm(index, setTerms, onSearch)
                            }
                            className="search-tag"
                            aria-label={`Remove search term: ${term}`}
                        >
                            <span className="search-tag__copy">{term}</span>

                            <SVG
                                className={concatClassNames([
                                    styles.closeIcon,
                                    "search-tag__close",
                                ])}
                                role="presentation"
                                src={iconXClose}
                                aria-hidden="true"
                            />
                        </button>
                    ))}
                    <button
                        onClick={() => handleClearAll(setTerms, onSearch)}
                        className="search-tag search-tag--clear"
                    >
                        {t`clear all tags`}
                    </button>
                </section>
            )}
        </>
    );
};
